import Model from "./model";
import { normalize } from "../utils";
import Notification from "../singleton/notification";
import LinkedField from "../singleton/linked-field";
import Field from "../singleton/field";
import Entity from "../singleton/entity";
import Documents from "../singleton/documents";
import DocumentValue from "../singleton/document-value";
import IRecordResponse from "../interface/record-response";
import ILinkedInactive from "../interface/linked-inactive";
import ILinkedFieldOption from "../interface/linked-field-option";
import IEntity from "../interface/entity";
import IDocumentValue from "../interface/document-value";
import IDocument from "../interface/document";
import ErrorCode from "../enum/error-code";

export default class RecordClass {

    public readonly entityId: number;

    public readonly recordId: number;

    private entity: IEntity;

    private model: Model;

    private recordTitle: string = '';

    private base: IDocument = {};

    private active: boolean = true;

    constructor(entity: IEntity, doc: IDocument) {
        this.entity = entity;
        this.entityId = entity.id;
        this.recordId = doc.id as number;
        this.base = Documents.getEmpty(this.entity);
        this.model = new Model(this.entity, doc, this.base);
        Documents.setLinkedFieldValues(this.entity, [this.model.data]);
        this.setRecordTitle();
    }

    /* Public Methods */

    public getClientResponse(): IRecordResponse {
        return {
            record: this.model ? this.model.data : null,
            isLoading: false,
            entityId: this.entity.id,
            recordId: this.recordId,
            recordTitle: this.recordTitle
        };
    }

    /* Public Methods - Documents control */

    public async documentDuplicate(): Promise<IDocument> {
        return await Documents.create(this.entity.id, this.model.getRaw());
    }

    public async documentUpdate(docId: number, fieldId: number, value: IDocumentValue): Promise<IDocument> {
        if (this.model.id === docId) {
            return await Documents.update(this.model.getRaw(), this.entity.id, fieldId, value);
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    public getDefaultValue(fieldId: number): IDocumentValue | null {
        const ref: number[] | null = Field.getDefaultValue(fieldId);
        if (ref) {
            return DocumentValue.getByPathSync(this.model.data, ref);
        }
        return null;
    }

    /* Public Methods - Documents notification */

    public async onDocumentsUpdate(docs: IDocument[]): Promise<void> {
        for (let i = 0; i < docs.length; i++) {
            if (this.model && this.model.id === docs[0].id) {
                this.model.setData(docs[0]);
                await Documents.loadRelatedDocs(this.entity, [this.model.data]);
                this.model.setData(this.model.data);
                this.model.calcFormulas();
                this.model.resetFormatted();
                Documents.setLinkedFieldValues(this.entity, [this.model.data]);
                this.setRecordTitle();
                this.notification();
                break;
            }
        }
    }

    /* Public Methods - Dictionary control */

    public async onEntityReset(): Promise<void> {
        const entity: IEntity | null = Entity.get(this.entity.id);
        if (entity) {
            this.base = Documents.getEmpty(this.entity);
            this.entity = entity;
            this.model.resetEntity(this.entity, this.base);
            await Documents.loadRelatedDocs(this.entity, [this.model.data]);
            this.model.setData(this.model.data);
            Documents.setLinkedFieldValues(this.entity, [this.model.data]);
            this.notification();
        }
    }

    /* Public Methods - Linked Field calls */

    public isLinkedActive(fieldId: number): ILinkedInactive[] {
        return LinkedField.isActive(fieldId, this.model.getRaw());
    }

    public async getLinkedOptions(fieldId: number): Promise<ILinkedFieldOption[]> {
        return await LinkedField.getOptions(fieldId, this.model.getRaw());
    }

    /* Public Methods - Special calls */

    public async getSimilarValues(fieldId: number, ptrn: string): Promise<string[]> {
        const pattern: string = normalize(ptrn.toLowerCase().trim());
        let docs: IDocument[] = await Documents.loadDocuments(this.entity.id, {
            fields: [fieldId]
        });
        const result: string[] = [];
        docs.forEach((doc: IDocument) => {
            const value: IDocumentValue = doc[`c${fieldId}`];
            if (value && typeof value === 'string') {
                const formatted: string = normalize(value.toLowerCase().trim());
                if (formatted.startsWith(pattern)) {
                    result.push(value);
                }
            }
        });
        return result;
    }

    /* Public Methods - System */

    public deactivate(): void {
        this.active = false;
    }

    /* Private Methods */

    private notification(): void {
        if (this.active) {
            Notification.sendRecordMessage(this.getClientResponse());
        }
    }

    private setRecordTitle(): void {
        this.recordTitle = Documents.getTitle(this.model.data, this.entity.id);
    }

}